import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

/**
 * Sanitisation schema that lets authors mix inline HTML into their Markdown
 * (divs, spans, callouts, details/summary, basic styling) while still stripping
 * dangerous content: <script>, event handlers (onClick, onError, …) and
 * javascript: URLs are removed by rehype-sanitize regardless of this schema.
 */
const knowledgeSanitizeSchema = {
    ...defaultSchema,
    tagNames: [
        ...(defaultSchema.tagNames ?? []),
        'div',
        'span',
        'section',
        'article',
        'header',
        'footer',
        'aside',
        'figure',
        'figcaption',
        'details',
        'summary',
        'mark',
        'small',
        'sub',
        'sup',
        'u',
        'kbd',
        'abbr',
        'time',
    ],
    attributes: {
        ...defaultSchema.attributes,
        '*': [...(defaultSchema.attributes?.['*'] ?? []), 'className', 'class', 'style', 'id', 'title'],
    },
};

function headingId(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
}

export function plainText(children: ReactNode): string {
    if (typeof children === 'string' || typeof children === 'number') {
        return String(children);
    }

    if (Array.isArray(children)) {
        return children.map(plainText).join('');
    }

    return '';
}

interface KnowledgeMarkdownProps {
    children: string;
    className?: string;
}

export function KnowledgeMarkdown({ children, className }: KnowledgeMarkdownProps) {
    return (
        <div className={cn('kb-markdown min-w-0 text-[15px]', className)}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, [rehypeSanitize, knowledgeSanitizeSchema]]}
                components={{
                    h1: ({ children: c }) => <h1 className="mt-10 mb-4 text-3xl leading-tight font-semibold">{c}</h1>,
                    h2: ({ children: c }) => {
                        const text = plainText(c);
                        return (
                            <h2 id={headingId(text)} className="mt-9 scroll-mt-28 text-2xl leading-tight font-semibold first:mt-0">
                                {c}
                            </h2>
                        );
                    },
                    h3: ({ children: c }) => {
                        const text = plainText(c);
                        return (
                            <h3 id={headingId(text)} className="mt-8 scroll-mt-28 text-xl leading-tight font-semibold">
                                {c}
                            </h3>
                        );
                    },
                    p: ({ children: c }) => <p className="text-foreground my-4 leading-7">{c}</p>,
                    ul: ({ children: c }) => <ul className="text-foreground my-4 list-disc space-y-2 pl-6">{c}</ul>,
                    ol: ({ children: c }) => <ol className="text-foreground my-4 list-decimal space-y-2 pl-6">{c}</ol>,
                    li: ({ children: c }) => <li className="leading-7">{c}</li>,
                    a: ({ children: c, href }) => (
                        <a href={href} className="text-primary font-medium underline-offset-4 hover:underline">
                            {c}
                        </a>
                    ),
                    blockquote: ({ children: c }) => (
                        <blockquote className="border-primary/40 text-muted-foreground my-6 border-l-4 pl-4">{c}</blockquote>
                    ),
                    code: ({ children: c, className: codeClass }) => (
                        <code className={cn('bg-muted rounded px-1.5 py-0.5 font-mono text-sm', codeClass)}>{c}</code>
                    ),
                    pre: ({ children: c }) => <pre className="bg-muted my-5 overflow-x-auto rounded-lg p-4 text-sm">{c}</pre>,
                    table: ({ children: c }) => <table className="my-6 w-full border-collapse text-sm">{c}</table>,
                    th: ({ children: c }) => <th className="border px-3 py-2 text-left font-semibold">{c}</th>,
                    td: ({ children: c }) => <td className="border px-3 py-2 align-top">{c}</td>,
                    hr: () => <hr className="border-border my-8" />,
                    img: ({ src, alt }) => <img src={src} alt={alt ?? ''} className="bg-card my-5 max-w-full rounded-xl border" />,
                }}
            >
                {children}
            </ReactMarkdown>
        </div>
    );
}
